/**
 * SB2-W-16 — Project state chip with Apple-color-coded background.
 *
 * Per Christian's locked colour matrix in `03-page_projects.md` §Table
 * column 8:
 *   white  ← OFFER, DATA_IMPORT
 *   yellow ← DATA_VALIDATION_CUSTOMER, PREPARATION, EXECUTION,
 *            ACCOUNTING, PAYMENT
 *   green  ← FINISHED
 *   red    ← WITHDRAWN, REJECTED
 *
 * Maps each band to the corresponding chip palette in
 * `packages/ui/src/theme/tokens/color.ts` (`chip.success / warning /
 * error / default`).
 */
import { type ReactElement } from "react";
import { Chip } from "@mui/material";
import type { ProjectState } from "./types.js";
import {
  PROJECT_STATE_COLOR,
  PROJECT_STATE_LABEL,
  projectBandStyle,
} from "./projectStateColors.js";

/**
 * Expose the colour band as a `data-color` attribute on the rendered
 * chip so Playwright assertions can read the band directly without
 * scraping CSS variables. Christian's AC9 matrix asserts band per
 * state; that selector path is the smallest stable one.
 *
 * The colour matrix + per-band styling now live in `projectStateColors`
 * so the Ongoing-Projects distribution band (R0627-02/03) and this chip
 * share ONE source of truth.
 */
export function ProjectStatusChip({
  state,
}: {
  state: ProjectState;
}): ReactElement {
  const color = PROJECT_STATE_COLOR[state];
  return (
    <Chip
      size="small"
      label={PROJECT_STATE_LABEL[state]}
      data-state={state}
      data-color={color}
      sx={(t) => projectBandStyle(t, color)}
    />
  );
}
